import { Box, IconButton, type SpeedDialActionProps, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { SpeedDialAction } from "components";
import { TooltipHotkey } from "components/tooltipHotkey";
import { featuresConfig } from "config/features";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchHighlightCollections, useHighlightCollections } from "contexts/highlightCollections";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { selectMainObject, selectMainObjects } from "features/render";
import { selectIsProcoreDesign } from "slices/explorer";
import { isMacintosh } from "utils/misc";
import { hideObjects, showObjects } from "utils/objectActions";

type Props = SpeedDialActionProps;

export function HideSelected({ newDesign, ...props }: Props & { newDesign?: boolean }) {
    const { t } = useTranslation();
    const { nameKey, Icon } = featuresConfig["hideSelected"];
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);

    const { idArr: highlighted } = useHighlighted();
    const { ids: hiddenIdsMap, idArr: hidden, lastHiddenIds } = useHidden();
    const {
        secondaryHighlight: { idArr: currentSecondaryHighlightIds },
    } = useHighlightCollections();
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchSelectionBasket = useDispatchSelectionBasket();

    const hasSelected = highlighted.length > 0 || mainObject !== undefined;
    const disabled = !hasSelected && !hidden.length;

    const toggleHideSelected = () => {
        if (hasSelected) {
            hideObjects(highlighted, {
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                dispatchHighlightCollections,
                currentSecondaryHighlightIds,
                currentMainObject: mainObject,
                currentMainObjects: mainObjects,
                currentHiddenIds: hiddenIdsMap,
                currentLastHiddenIds: lastHiddenIds,
                preserveSelection: isProcoreDesign,
                includeMainObjectToHide: true,
            });
        } else if (hidden.length) {
            showObjects(hidden, {
                dispatch,
                dispatchHidden,
            });
        }
    };

    const title =
        !hasSelected && hidden.length ? (
            <Box display="flex" alignItems="center" gap={1}>
                {t("unhideAll")}
                <TooltipHotkey>Shift+H</TooltipHotkey>
            </Box>
        ) : (
            <Box display="flex" alignItems="center" gap={1}>
                {t(nameKey)}
                <TooltipHotkey>{isMacintosh ? "Cmd+H" : "Ctrl+H"}</TooltipHotkey>
            </Box>
        );

    if (newDesign) {
        return (
            <Tooltip title={title} placement="top">
                <Box>
                    <IconButton onClick={toggleHideSelected} disabled={disabled}>
                        <Icon />
                    </IconButton>
                </Box>
            </Tooltip>
        );
    }

    return (
        <SpeedDialAction
            {...props}
            data-test="hide-selected"
            FabProps={{ disabled, ...props.FabProps }}
            onClick={toggleHideSelected}
            title={disabled ? undefined : title}
            icon={<Icon />}
        />
    );
}
